import React, { useState, useCallback, useEffect, useMemo } from "react";
import { PropTypes } from "prop-types";
import {
  Box,
  Button,
  Grid,
  Hidden,
  FormControlLabel,
  Checkbox,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { difference, intersection, sortBy } from "lodash";
import DualListContainer from "../../components/DualList/DualListContainer";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { enterKeyCode, states } from "../../constants";
import ProcessingModal from "../../components/ProcessingModal/ProcessingModal";
import { TextField } from "formik-mui";
import { Search } from "@mui/icons-material";
import {
  PositionsFilter,
  LocationsFilter,
  GroupsFilter,
  DatePickerField,
  Select
} from "../../components/FormikFields";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import { isBeforeDate, isValidDate } from "../../utils/datetime";
import { useEmployeesAssignments } from "../../hooks/assignments";
import { useUsers } from "../../hooks/redux/useUsers";
import { useSnackbarNotifications } from "../../hooks/snackbar";
import SpecialAssignmentConfirmationModal from "../../components/WarningModal/SpecialAssignmentConfirmationModal";
import { useDispatch } from "react-redux";
import { enqueueInfoSnackbar } from "../../redux/actions/snackbarActions";

/**
 * Returns an object containing the properties that will be passed into
 * the DualListItem component
 */
const getListItemDetails = item => {
  return {
    id: item.id,
    name: item.fullName,
    firstName: item.firstName,
    lastName: item.lastName,
    subtitles: [
      item.position ? item.position.name : "",
      item.location ? item.location.name : "",
      item.isCompleted ? (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            fontStyle: "italic",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          noWrap
        >
          {item.isCompleted}
        </Typography>
      ) : (
        ""
      )
    ],
    highlighted: item.isAssigned,
    hideArrow: item.isCompleted
  };
};

export const includeEmployee = (
  employee,
  { position, location, group, state, fullName, hiredSince, hiredBefore },
  displayAssignedEmployeesOnly,
  hideCompletedAssignedEmployees,
  selected
) => {
  if (selected) {
    if (hideCompletedAssignedEmployees && employee.isCompleted) {
      return false;
    } else {
      return true;
    }
  }
  if (position && employee.position.id !== position) {
    return false;
  }
  if (location && employee.location.id !== location) {
    return false;
  }
  if (state && employee.location.state !== state) {
    return false;
  }
  if (group && group.employeesCount === 0) {
    // easy case for when a group has no users
    return false;
  }
  if (
    group &&
    !employee.groups.some(employeeGroup => employeeGroup.id === group.id)
  ) {
    return false;
  }
  if (
    fullName &&
    employee.fullName.toLowerCase().indexOf(fullName.toLowerCase()) === -1
  ) {
    return false;
  }
  if (hiredSince && isBeforeDate(employee.hireDate, hiredSince)) {
    return false;
  }
  if (hiredBefore && isBeforeDate(hiredBefore, employee.hireDate)) {
    return false;
  }
  if (displayAssignedEmployeesOnly && !employee.isAssigned) {
    return false;
  }
  return true;
};

const sortEmployees = employees =>
  sortBy(employees, [
    user => user.lastName.toLowerCase(),
    user => user.firstName.toLowerCase()
  ]);

const defaultFilterValues = {
  location: "",
  position: "",
  group: "",
  state: "",
  fullName: "",
  hiredSince: null,
  hiredBefore: null
};

const getSchema = t => {
  return Yup.object().shape({
    hiredSince: Yup.mixed().test(
      "hiredSince",
      t("validationMessages.validDate"),
      function() {
        return (
          !this.parent.hiredSince ||
          (isValidDate(this.parent.hiredSince) &&
            !isBeforeDate(this.parent.hiredSince, "01/01/1900"))
        );
      }
    ),
    hiredBefore: Yup.mixed().test(
      "hiredSince",
      t("validationMessages.validDate"),
      function() {
        return (
          !this.parent.hiredBefore ||
          (isValidDate(this.parent.hiredBefore) &&
            !isBeforeDate(this.parent.hiredBefore, "01/01/1900"))
        );
      }
    )
  });
};

const FormFilters = ({ onFilterEmployees, onClearAllFilters }) => {
  const { t } = useTranslation();

  const defaultFilterOption = {
    value: "",
    label: t("courseAssignments.stateFilterAllOption")
  };

  return (
    <Form>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={4} xl={3}>
          <Field
            fullWidth
            label={t("filterLabels.employeeName")}
            variant="filled"
            component={TextField}
            name="fullName"
            InputProps={{
              endAdornment: <Search color="action" />,
              disableUnderline: true,
              onKeyDown: event =>
                event.keyCode === enterKeyCode && onFilterEmployees()
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} xl={3}>
          <PositionsFilter />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} xl={3}>
          <LocationsFilter />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} xl={3}>
          <GroupsFilter getValue={group => group} />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} xl={3}>
          <Select
            disableUnderline
            name={"state"}
            menuItems={[defaultFilterOption].concat(states)}
            label={t("filterLabels.state")}
          />
        </Grid>
        <Hidden mdDown xlUp>
          <Grid item xs={12} sm={6} md={4} xl={3} />
        </Hidden>
        <Grid item xs={12} sm={6} lg={4} xl={3}>
          <Field
            name="hiredBefore"
            component={DatePickerField}
            label={t("filterLabels.hiredBefore")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4} xl={3}>
          <Field
            name="hiredSince"
            component={DatePickerField}
            label={t("filterLabels.hiredSince")}
          />
        </Grid>
        <Grid
          container
          item
          xs={12}
          lg={4}
          xl={3}
          sx={{ alignItems: "center" }}
        >
          <ButtonGroup align="right" flex={1}>
            <Button color="primary" onClick={() => onClearAllFilters()}>
              {t("courseSession.clearAllBtn")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className="wide"
              onClick={() => onFilterEmployees()}
            >
              {t("courseSession.filterListBtn")}
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Form>
  );
};

const AssignEmployeesForm = ({
  existingAssignmentId,
  onGoBack,
  setHasAssignedEmployees
}) => {
  const { t } = useTranslation();
  const [saveProcess, setSaveProcess] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [
    displayAssignedEmployeesOnly,
    setDisplayAssignedEmployeesOnly
  ] = useState(false);
  const [
    hideCompletedAssignedEmployees,
    setHideCompletedAssignedEmployees
  ] = useState(false);
  const [excludedEmployees, setExcludedEmployees] = useState([]);
  const [assignmentChanges, setAssignmentChanges] = useState({});

  const dispatch = useDispatch();

  const {
    employeesWithSpecialAssignment,
    employeesWithoutSpecialAssignment,
    employeesWithAssignment,
    employeesWithExclusion,
    employeesWithCompletedSpecialAssignment,
    updateEmployeesAssignments,
    isLoading: isLoadingAssignments,
    isUpdating: isUpdatingAssignments
  } = useEmployeesAssignments(existingAssignmentId);

  const {
    users: allEmployees,
    fetchUsers,
    isLoading: isLoadingUsers
  } = useUsers();

  const { displayErrorMessage } = useSnackbarNotifications();

  const isLoading = isLoadingUsers || isLoadingAssignments;

  useEffect(() => {
    if (!allEmployees) {
      fetchUsers();
    }
  }, []);

  const getEmployeeList = employees =>
    !!allEmployees && employees
      ? sortEmployees(
          employees.map(employeeId => {
            const employee = allEmployees.find(({ id }) => id == employeeId);
            return {
              ...employee,
              isAssigned: employeesWithAssignment.includes(employeeId),
              isCompleted: employeesWithCompletedSpecialAssignment.includes(
                employeeId
              )
                ? t("courseSession.completed")
                : false
            };
          })
        )
      : [];

  const initialUnassignedList = useMemo(
    () => getEmployeeList(employeesWithoutSpecialAssignment),
    [employeesWithoutSpecialAssignment, allEmployees]
  );

  const assignedListWithCompletions = useMemo(
    () =>
      getEmployeeList([
        ...employeesWithSpecialAssignment,
        ...employeesWithCompletedSpecialAssignment
      ]),
    [
      employeesWithAssignment,
      employeesWithSpecialAssignment,
      employeesWithCompletedSpecialAssignment,
      allEmployees
    ]
  );

  const filterEmployees = useCallback(
    employees =>
      sortEmployees(
        employees.filter(employee =>
          includeEmployee(
            employee,
            filters,
            displayAssignedEmployeesOnly,
            hideCompletedAssignedEmployees
          )
        )
      ),
    [filters, displayAssignedEmployeesOnly, hideCompletedAssignedEmployees]
  );

  const filterSelectedEmployees = useCallback(
    employees =>
      sortEmployees(
        employees.filter(employee =>
          includeEmployee(
            employee,
            filters,
            displayAssignedEmployeesOnly,
            hideCompletedAssignedEmployees,
            true
          )
        )
      ),
    [filters, displayAssignedEmployeesOnly, hideCompletedAssignedEmployees]
  );

  const getUpdatedAssignments = (initialEmployeeIds, updatedEmployees) => {
    const updatedEmployeeIds = updatedEmployees.map(({ id }) => id);
    const uncompletedEmployees = difference(
      updatedEmployeeIds,
      employeesWithCompletedSpecialAssignment
    );
    return difference(uncompletedEmployees, initialEmployeeIds);
  };

  const saveAssignmentChanges = currentChanges => {
    const changesToSave = currentChanges || assignmentChanges;
    setSaveProcess(true);
    updateEmployeesAssignments(existingAssignmentId, {
      add: changesToSave.add,
      remove: changesToSave.remove
    })
      .then(response => {
        if (response.errors) {
          displayErrorMessage(t("specialAssignments.errorSaving"));
        } else {
          setHasAssignedEmployees(!!response.assigned.length);
          dispatch(
            enqueueInfoSnackbar({
              message: t("employees.assignments.saveBulkSuccess"),
              options: {
                autoHideDuration: 7500
              }
            })
          );
        }
        setSaveProcess(false);
      })
      .catch(error => {
        displayErrorMessage(t("specialAssignments.errorSaving"));
        setSaveProcess(false);
      });
  };

  const handleSave = (unselectedItems, selectedItems) => {
    const assignmentsToCreate = getUpdatedAssignments(
      employeesWithSpecialAssignment,
      selectedItems
    );
    const assignmentsToDelete = getUpdatedAssignments(
      employeesWithoutSpecialAssignment,
      unselectedItems
    );

    setAssignmentChanges({
      add: assignmentsToCreate,
      remove: assignmentsToDelete
    });

    const excludedIds = intersection(
      assignmentsToCreate,
      employeesWithExclusion
    );
    const exclusionsWillBeDestroyed = excludedIds.map(employeeId => {
      const employee = allEmployees.find(({ id }) => id === employeeId);
      return {
        id: employee.id,
        name: employee.fullName
      };
    });
    setExcludedEmployees(exclusionsWillBeDestroyed);

    if (exclusionsWillBeDestroyed.length > 0) {
      setOpenModal(true);
    } else {
      saveAssignmentChanges({
        add: assignmentsToCreate,
        remove: assignmentsToDelete
      });
    }
    return;
  };

  return (
    <div>
      <ProcessingModal
        open={saveProcess}
        messageText={t("courseAssignments.processingMessage")}
      />
      <SpecialAssignmentConfirmationModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={() => {
          setOpenModal(false);
          saveAssignmentChanges();
        }}
        employees={excludedEmployees}
      />
      <Box mt={3} flex={1} display="flex">
        <DualListContainer
          initialSelectedItems={assignedListWithCompletions}
          initialUnselectedItems={initialUnassignedList}
          filters={
            <Formik
              initialValues={defaultFilterValues}
              validationSchema={getSchema(t)}
              enableReinitialize
              onSubmit={(values, { setSubmitting }) => {
                setFilters({ ...values });
                setSubmitting(false);
              }}
            >
              {({ submitForm, resetForm }) => (
                <FormFilters
                  onFilterEmployees={() => submitForm()}
                  onClearAllFilters={() => {
                    setFilters(defaultFilterValues);
                    resetForm();
                  }}
                />
              )}
            </Formik>
          }
          filterUnselected={filterEmployees}
          filterSelected={filterSelectedEmployees}
          rowHeight={item => (!!item.subtitles?.[2] ? 110 : 89)}
          getListItemDetails={getListItemDetails}
          selectedListHeader={`${t("specialAssignments.assignedEmployees")}`}
          unselectedListHeader={`${t(
            "specialAssignments.unassignedEmployees"
          )}`}
          onSave={handleSave}
          onCancel={onGoBack}
          unselectedListAction={
            <FormControlLabel
              control={
                <Checkbox
                  onChange={({ target: { checked } }) =>
                    setDisplayAssignedEmployeesOnly(checked)
                  }
                  checked={displayAssignedEmployeesOnly}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {t("courseSession.displayAssignedEmployeesOnly")}
                </Typography>
              }
            />
          }
          selectedListAction={
            <FormControlLabel
              control={
                <Checkbox
                  onChange={({ target: { checked } }) =>
                    setHideCompletedAssignedEmployees(checked)
                  }
                  checked={hideCompletedAssignedEmployees}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {t("courseSession.displayCompletedAssignedEmployees")}
                </Typography>
              }
            />
          }
          isLoadingItems={isLoading}
          isSaving={isUpdatingAssignments}
          disabled={isLoading || isUpdatingAssignments}
        />
      </Box>
    </div>
  );
};

export default AssignEmployeesForm;

AssignEmployeesForm.propTypes = {
  existingAssignmentId: PropTypes.string,
  onGoBack: PropTypes.func
};
